import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTimezone } from '../contexts/TimezoneContext';
import RecordingPlayer from './RecordingPlayer';
import VerificationSection from './VerificationSection';
import DownloadSection from './DownloadSection';
import { Icon } from './reportIcons';
import { FiInfo } from 'react-icons/fi';
import aiAutomationApi from '../utils/aiAutomationApi';
import useInterviewResult from '../pages/interview/hooks/useInterviewResult';

/**
 * Shared interview result / meeting analysis modal.
 *
 * One professional, theme-aware modal used everywhere a candidate's interview outcome is shown:
 *   • Interview pipeline "View result"      (AI result)
 *   • Candidate profile "Meeting Analysis"  (AI result — via <AIInterviewResultView/>)
 *   • Chat card "Meeting Analysis"           (AI result or manual meeting-analysis notes)
 *
 * Pass `aiResult` (the /ai-result payload) for a fully-AI interview, or `analysis` (the
 * meeting_analysis_json) for a human meeting. The body adapts to whichever is present.
 */

// ── transcript extraction (self-contained) ─────────────────────────────────────────────
const normSpeaker = (value) => {
  const l = String(value || '').toLowerCase();
  if (l.includes('candidate') || l.includes('applicant') || l === 'user') return 'Candidate';
  if (l.includes('interviewer') || l.includes('assistant') || l.includes('wiza') || l.includes('ai')) return 'Wiza';
  return value || 'Wiza';
};
const fmtTime = (value, timezone) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  // Always 12-hour (e.g. "2:30 PM"), regardless of the viewer's locale default.
  if (!Number.isNaN(d.getTime()) && /[-:T]/.test(raw)) {
    const options = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timezone) options.timeZone = timezone;
    return d.toLocaleTimeString([], options);
  }
  const m = raw.match(/(\d{1,2}):(\d{2})/);
  if (!m) return '';
  const h = parseInt(m[1], 10);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};
const parseLine = (line) => {
  const m = String(line || '').trim().match(/^(?:\[?[^\]]*\]?\s*)?(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
  if (!m || !m[2].trim()) return null;
  const speaker = normSpeaker(m[1]);
  return { speaker, role: String(speaker).toLowerCase(), text: m[2].trim(), timeStr: '' };
};
function extractTranscript(result, timezone) {
  let raw = result?.turns || result?.messages || result?.transcript || result?.conversation || null;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { /* leave as string, handled below */ }
  }
  if (Array.isArray(raw)) {
    return raw.map((t) => {
      if (typeof t === 'string') return parseLine(t);
      if (!t || typeof t !== 'object') return null;
      const text = String(t.text || t.content || t.message || t.transcript || '').trim();
      if (!text) return null;
      const speaker = normSpeaker(String(t.role || t.speaker || t.author || ''));
      return { speaker, role: String(speaker).toLowerCase(), text, pasted: !!(t.pasted || t.was_pasted), timeStr: fmtTime(t.time || t.timeStr || t.created_at || t.timestamp_iso || '', timezone) };
    }).filter(Boolean);
  }
  const text = String(result?.transcript_text || result?.transcript || '');
  return text.split('\n').map((l) => l.trim()).filter(Boolean).map(parseLine).filter(Boolean);
}
const isCandidateEntry = (entry) => {
  const role = String(entry?.role || entry?.speaker || '').toLowerCase();
  return role.includes('candidate') || role.includes('applicant') || role === 'user';
};

const compactText = (value, limit = 220) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit - 1).trim()}…` : text;
};

const candidatePassageFromRow = (row) => {
  const direct = row?.candidate_spoken_passage || row?.candidate_answer;
  if (direct) return String(direct).trim();
  const excerpt = String(row?.transcript_excerpt || '');
  const match = excerpt.match(/Candidate:\s*([\s\S]*)/i);
  if (match?.[1]) return match[1].trim();
  return '';
};

const practicalTaskPrompt = (task) => {
  const snapshot = task?.task || {};
  return String(
    task?.candidate_prompt
    || snapshot.candidate_prompt
    || snapshot.prompt
    || snapshot.task_prompt
    || snapshot.question
    || ''
  ).trim();
};

const normalizeCompetencyCoverage = (value, fallbackScores = []) => {
  const raw = Array.isArray(value)
    ? { items: value }
    : (value && typeof value === 'object' ? value : {});
  const rawItems = Array.isArray(raw.items) ? raw.items : [];
  const items = rawItems.map((item, index) => {
    const status = String(item?.status || item?.coverage_status || item?.score_status || 'not_assessed');
    const weight = Number(item?.weight || 0);
    return {
      ...item,
      key: item?.key || item?.competency_key || item?.label || `competency_${index + 1}`,
      label: item?.label || item?.competency_label || item?.competency_key || `Competency ${index + 1}`,
      weight: Number.isFinite(weight) ? weight : 0,
      status,
      planned_qids: Array.isArray(item?.planned_qids) ? item.planned_qids : (Array.isArray(item?.planned_questions) ? item.planned_questions : []),
      evaluated_qids: Array.isArray(item?.evaluated_qids) ? item.evaluated_qids : (Array.isArray(item?.evaluated_questions) ? item.evaluated_questions : []),
      reason: item?.reason || item?.gap || item?.missing_reason || '',
    };
  });
  const assessed = Number.isFinite(Number(raw.assessed))
    ? Number(raw.assessed)
    : items.filter((item) => item.status === 'assessed').length;
  const partiallyAssessed = Number.isFinite(Number(raw.partially_assessed))
    ? Number(raw.partially_assessed)
    : items.filter((item) => item.status === 'partially_assessed').length;
  const notAssessed = Number.isFinite(Number(raw.not_assessed))
    ? Number(raw.not_assessed)
    : Math.max(0, (items.length || fallbackScores.length) - assessed - partiallyAssessed);
  const totalWeight = Number.isFinite(Number(raw.total_weight))
    ? Number(raw.total_weight)
    : items.reduce((sum, item) => sum + Number(item.weight || 0), 0);
  const evaluatedWeight = Number.isFinite(Number(raw.evaluated_weight))
    ? Number(raw.evaluated_weight)
    : items.filter((item) => ['assessed', 'partially_assessed'].includes(item.status)).reduce((sum, item) => sum + Number(item.weight || 0), 0);
  return {
    ...raw,
    items,
    total: Number.isFinite(Number(raw.total)) ? Number(raw.total) : (items.length || fallbackScores.length),
    assessed,
    partially_assessed: partiallyAssessed,
    not_assessed: notAssessed,
    total_weight: totalWeight,
    evaluated_weight: evaluatedWeight,
  };
};

const candidateIntelText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return compactText(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => candidateIntelText(item)).filter(Boolean).slice(0, 3).join('; ');
  }
  if (typeof value !== 'object') return '';

  const direct = value.claim || value.name || value.title || value.label || value.skill || value.degree || value.role
    || value.criterion || value.requirement || value.jd_requirement || value.claim || value.issue;
  const details = [
    value.company || value.institution || value.context || value.dates || value.status,
    value.description || value.evidence || value.candidate_evidence || value.missing_information || value.change || value.movement || value.scope,
    value.risk || value.probe || value.reason,
    value.graduation_date,
    value.evidence_strength ? `${value.evidence_strength} CV claim` : '',
  ].map((item) => candidateIntelText(item)).filter(Boolean);

  if (direct) {
    return compactText([candidateIntelText(direct), ...details].filter(Boolean).join(' - '));
  }

  const preferredPairs = [
    ['degree', 'Degree'], ['institution', 'Institution'], ['status', 'Status'], ['dates', 'Dates'],
    ['company', 'Company'], ['title', 'Title'], ['description', 'Details'], ['context', 'Context'],
    ['evidence', 'Evidence'], ['candidate_evidence', 'Evidence'], ['missing_information', 'Missing'],
    ['change', 'Change'], ['movement', 'Movement'], ['scope', 'Scope'],
  ].map(([key, label]) => value[key] ? `${label}: ${candidateIntelText(value[key])}` : '')
    .filter(Boolean);

  if (preferredPairs.length) return compactText(preferredPairs.slice(0, 3).join('; '));
  return compactText(Object.values(value).map((item) => candidateIntelText(item)).filter(Boolean).slice(0, 3).join('; '));
};

const candidateIntelItems = (items, limit = 5) => {
  const arr = Array.isArray(items) ? items : (items ? [items] : []);
  return arr.map((item) => candidateIntelText(item)).filter(Boolean).slice(0, limit);
};

// A themed line-icon for a scoring criterion, chosen by keyword from its label (no emojis).
const CriterionIcon = ({ label, className }) => {
  const l = String(label || '').toLowerCase();
  const path = (l.includes('technical') || l.includes('skill') || l.includes('coding') || l.includes('knowledge'))
    ? <path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13.5 5l-3 14" strokeLinecap="round" strokeLinejoin="round" />
    : (l.includes('experience') || l.includes('relevance') || l.includes('background') || l.includes('domain'))
      ? <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7" strokeLinecap="round" /></>
      : (l.includes('problem') || l.includes('solv') || l.includes('analy') || l.includes('critical'))
        ? <><path d="M9 18h6M10 21h4M12 2a7 7 0 0 0-4 12.6c.6.5 1 1.2 1 2h6c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" strokeLinecap="round" strokeLinejoin="round" /></>
        : (l.includes('commun') || l.includes('clarity') || l.includes('articul') || l.includes('language'))
          ? <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
          : (l.includes('behav') || l.includes('team') || l.includes('cultur') || l.includes('collab') || l.includes('attitude'))
            ? <><circle cx="9" cy="8" r="3" /><path d="M15 11a3 3 0 1 0-2.5-4.6M3.5 20a5.5 5.5 0 0 1 11 0M17 20a5 5 0 0 0-3.5-4.8" strokeLinecap="round" strokeLinejoin="round" /></>
            : <path d="M12 2l2.9 6.3 6.6.6-5 4.4 1.5 6.7L12 17l-5 3.3 1.5-6.7-5-4.4 6.6-.6z" strokeLinecap="round" strokeLinejoin="round" />;
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">{path}</svg>;
};

// ── AI interview result body (comprehensive) ────────────────────────────────────────────
export function AIInterviewResultView({ result, onRecalculate, recalculating = false, waiting = false }) {
  const [expanded, setExpanded] = useState(false);
  const [section, setSection] = useState('report');   // sidebar tab: report | screen | camera | verification | download
  const [reportTab, setReportTab] = useState('overview');
  const [scoreDetailsOpen, setScoreDetailsOpen] = useState(false);
  const contentRef = useRef(null);
  const { settings } = useSettings();
  const { ianaTimezone } = useTimezone();
  const primary = settings?.theme?.colors?.primary || '#7c3aed';
  const data = result || {};
  const evaluation = data.evaluation || {};
  const hrReport = evaluation.hr_report || {};
  const hrQuestions = hrReport.questions || {};
  const sectionScores = hrReport.sections || evaluation.section_scores || [];
  const weightCoverage = hrReport.weight_coverage || {};
  const technicalAvailability = hrReport.technical_availability || {};
  const refreshStatus = String(data.evaluation_status || data.refresh_status || '').toLowerCase();
  const refreshRequestedBy = String(data.refresh_requested_by || '').toLowerCase();
  const statusRunning = refreshStatus === 'queued' || refreshStatus === 'running';
  const refreshRunning = statusRunning && (recalculating || refreshRequestedBy.startsWith('refresh') || refreshRequestedBy === 'api');
  const initialAnalysisRunning = statusRunning && !refreshRunning;
  const refreshFailed = refreshStatus === 'failed';
  const metadata = data.metadata || {};
  // Recording-player identifiers (present on AI-interview results). If any is missing, the player
  // renders its own "unavailable" scenario rather than erroring.
  const recCompanyId = metadata.cid ?? metadata.company_id ?? data.company_id;
  const recSessionId = metadata.session_id ?? data.session_id;
  const recCandidateId = metadata.candidate_id ?? data.candidate_id;
  const recInterviewId = data.interview_id ?? metadata.interview_id;
  const canShowRecording = recCompanyId != null && recSessionId && recCandidateId;
  const coverage = evaluation.coverage || {};
  const questionCoverage = evaluation.question_coverage || {};
  const rawCompetencyCoverage = evaluation.competency_coverage;
  const completionSummary = evaluation.completion_summary || {};
  const sourceCoverage = evaluation.source_coverage || completionSummary.source_coverage || {};
  const progressionIssue = evaluation.progression_issue || completionSummary.progression_issue || sourceCoverage.progression_issue || '';
  const timeAttribution = evaluation.time_attribution || {};
  const incomplete = evaluation.incomplete || evaluation.reason === 'insufficient_coverage';
  const lowCoverage = !!evaluation.low_coverage;   // scored, but on partial coverage → warn alongside the score
  const decision = String(evaluation.decision || '').toLowerCase();
  const recommendationCategory = evaluation.recommendation_category || evaluation.decision_label || '';
  const recommendationKey = String(recommendationCategory || decision).toLowerCase();
  const humanReviewRequired = !!evaluation.human_review_required;
  const recommendationUncertainty = evaluation.recommendation_uncertainty || '';

  // Decision → colour set. `hex` drives the SVG ring / inline styles; the rest are tailwind tokens.
  const dt = recommendationKey.includes('fit') && !recommendationKey.includes('mismatch')
    ? { hex: '#10b981', text: 'text-emerald-600 dark:text-emerald-400', soft: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500' }
    : recommendationKey.includes('mismatch') || decision === 'reject'
      ? { hex: '#ef4444', text: 'text-rose-600 dark:text-rose-400', soft: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', dot: 'bg-rose-500' }
      : { hex: '#f59e0b', text: 'text-amber-600 dark:text-amber-400', soft: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', dot: 'bg-amber-500' };

  const transcriptEntries = useMemo(() => extractTranscript(data, ianaTimezone), [data, ianaTimezone]);
  const rawPerformanceScore = evaluation.performance_score ?? evaluation.overall_score;
  const score = rawPerformanceScore == null ? null : Number(rawPerformanceScore);
  const maxScore = Number(evaluation.overall_max || 100);
  const thresholds = evaluation.thresholds || {};
  const selectThreshold = Number.isFinite(Number(thresholds.select)) ? Number(thresholds.select) : 70;
  const maybeThreshold = Number.isFinite(Number(thresholds.maybe)) ? Number(thresholds.maybe) : 50;
  const hasScore = evaluation.available && score != null && Number.isFinite(score);
  const provisionalScore = !!evaluation.performance_score_provisional;
  const scorePercent = Number.isFinite(score) && maxScore > 0 ? Math.min(100, Math.max(0, (score / maxScore) * 100)) : 0;
  const ringHex = hasScore ? dt.hex : '#cbd5e1';
  const needsReview = humanReviewRequired || decision === 'maybe';
  const RING = 128, STROKE = 10, R = (RING - STROKE) / 2, CIRC = 2 * Math.PI * R;
  const RING_OFFSET = CIRC * (1 - (hasScore ? scorePercent : 0) / 100);

  // Auditable facts only — no inference. "asked" = main questions; "answered" = those with a substantive reply.
  const endReason = String(metadata.end_reason || coverage.end_reason || '').toLowerCase();
  const completedOk = endReason === 'completed' || endReason === 'time_exceeded' || (!endReason && !incomplete);
  // Distinguish a DELIBERATE candidate end from genuine abandonment / technical failure — the backend
  // now sends `incomplete_kind`; fall back to end_reason parsing for older results.
  const incompleteKind = String(evaluation.incomplete_kind || '');
  const endedByCandidate = incompleteKind === 'ended_by_candidate'
    || endReason.includes('candidate') || endReason === 'aborted';
  const isTechnicalEnd = incompleteKind === 'technical_failure' || endReason === 'technical_failure';
  const completionReason = String(completionSummary.completion_reason || questionCoverage.completion_reason || '').trim();
  const completionLabel = completionReason ? completionReason.replaceAll('_', ' ').toLowerCase().replace(/(^|\s)\S/g, (s) => s.toUpperCase())
    : endReason === 'completed' ? 'Completed'
    : endReason === 'time_exceeded' ? 'Time limit reached'
    : endedByCandidate ? 'Ended early by candidate'
    : isTechnicalEnd ? 'Technical issue'
    : endReason.startsWith('abandoned') ? 'Left / abandoned'
    : endReason ? endReason.replaceAll('_', ' ') : 'Completed';
  const askedCount = hrQuestions.normal_asked ?? questionCoverage.asked ?? coverage.main_questions_asked ?? metadata.questions_asked ?? 0;
  const questionCountMode = String(questionCoverage.question_count_mode || completionSummary.question_count_mode || '').toLowerCase();
  const coverageMinimumMode = questionCountMode === 'coverage_minimum' || questionCountMode === 'unrestricted';
  const baselinePlannedCount = Number(hrQuestions.baseline_planned ?? questionCoverage.baseline_planned ?? questionCoverage.planned ?? coverage.planned_questions ?? 0);
  const baselineAskedCount = Number(hrQuestions.baseline_asked ?? questionCoverage.baseline_asked ?? sourceCoverage.ai?.asked ?? 0);
  const baselineEvaluatedCount = Number(hrQuestions.baseline_evaluated ?? questionCoverage.baseline_evaluated ?? sourceCoverage.ai?.evaluated ?? 0);
  const repairEvaluatedCount = Number(hrQuestions.repair_evaluated ?? questionCoverage.coverage_repair_evaluated ?? sourceCoverage.coverage_repair?.evaluated ?? 0);
  const repairAskedCount = Number(hrQuestions.repair_asked ?? questionCoverage.coverage_repair_asked ?? 0);
  const repairPlannedCount = Number(questionCoverage.coverage_repair_planned || 0);
  // In UNRESTRICTED mode the question_plan is a coverage SUPERSET (one+ per JD skill), not a target —
  // the AI asks only enough to cover the role within the duration, then wraps. So showing "asked/planned"
  // (e.g. 7/26) is misleading: the "planned" number is not an expected count. Suppress it in that mode
  // and let the skill-coverage tile convey completeness instead.
  const unrestricted = !!coverage.unrestricted;
  const plannedCount = unrestricted && !coverageMinimumMode ? null : (coverageMinimumMode ? baselinePlannedCount : (questionCoverage.planned ?? coverage.planned_questions));
  const answeredCount = hrQuestions.normal_answered ?? questionCoverage.answered ?? coverage.answered ?? 0;
  const evaluatedQuestionCount = hrQuestions.normal_evaluated ?? questionCoverage.evaluated ?? answeredCount;
  const skippedQuestionCount = questionCoverage.skipped ?? 0;
  const notReachedQuestionCount = questionCoverage.not_reached ?? 0;
  const practicalTasksPlanned = Number(coverage.practical_tasks_planned || 0);
  const practicalTasksSubmitted = Number(hrQuestions.task_submitted ?? coverage.practical_tasks_submitted ?? 0);
  const practicalTasksEvaluated = Number(hrQuestions.task_evaluated ?? sourceCoverage.practical_task?.evaluated ?? 0);
  const followupOrReaskCount = Number(questionCoverage.followup_or_reask_count ?? coverage.followup_or_reask_count ?? 0);
  const sourceCoverageRows = [
    { key: 'recruiter', label: 'Recruiter questions', data: sourceCoverage.recruiter, metric: 'asked' },
    { key: 'ai', label: 'AI baseline questions', data: sourceCoverage.ai, metric: 'asked' },
    { key: 'coverage_repair', label: 'Coverage repair', data: sourceCoverage.coverage_repair, metric: 'asked' },
    { key: 'practical_task', label: 'Practical task', data: sourceCoverage.practical_task, metric: 'submitted' },
  ].filter((row) => Number(row.data?.planned || 0) > 0);
  const requiredSkills = Array.isArray(coverage.required_skills) ? coverage.required_skills : [];
  const coveredSkills = Array.isArray(coverage.covered_skills) ? coverage.covered_skills : [];
  // Asked vs Assessed split: "asked" = the AI reached & asked the topic; "assessed" = the candidate
  // answered it well enough to evaluate. Two metrics tell the recruiter exactly what happened.
  const topicsStatus = Array.isArray(coverage.topics_status) ? coverage.topics_status : [];
  const topicsRequiredCount = coverage.topics_required_count ?? requiredSkills.length;
  const topicsAskedCount = coverage.topics_asked_count ?? coveredSkills.length;
  const topicsAssessedCount = coverage.topics_assessed_count ?? coveredSkills.length;
  const coverageCause = coverage.coverage_cause || '';
  const elapsedMinutes = metadata.started_at && metadata.ended_at
    ? Math.max(0, Math.round((new Date(metadata.ended_at) - new Date(metadata.started_at)) / 60000))
    : null;
  const _durMin = elapsedMinutes != null && Number.isFinite(elapsedMinutes)
    ? elapsedMinutes
    : (metadata.duration_minutes != null ? Number(metadata.duration_minutes) : null);
  const durationSubLabel = elapsedMinutes != null && Number.isFinite(elapsedMinutes) ? 'actual session' : 'scheduled';
  const durationLabel = _durMin != null && Number.isFinite(_durMin) ? `${_durMin}m` : '—';

  const rejoinCount = Number(metadata.integrity?.rejoins || metadata.rejoins || 0);
  const silenceCount = Number(metadata.integrity?.silence_checkins || metadata.silence_checkins || 0);
  const pastedCount = Number(metadata.integrity?.pasted_answers || metadata.pasted_answers || 0);
  const sessionSignals = [
    rejoinCount > 0 ? { label: 'Rejoin', value: `${rejoinCount}×`, cls: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300' } : null,
    silenceCount > 0 ? { label: 'Silence prompts', value: `${silenceCount}×`, cls: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300' } : null,
    pastedCount > 0 ? { label: 'Pasted answers', value: `${pastedCount}×`, cls: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300' } : null,
  ].filter(Boolean);
  const unavailableReason = waiting ? 'Scoring has not finished. Check status to retrieve the latest result.' : initialAnalysisRunning
    ? 'Analysis is being generated. This report will update automatically when scoring is ready.'
    : evaluation.error || (incomplete ? 'Insufficient coverage for fair scoring' : 'Score not available yet');
  const visible = expanded ? transcriptEntries : transcriptEntries.slice(0, 6);
  const mandatoryCriteria = Array.isArray(evaluation.mandatory_criteria) ? evaluation.mandatory_criteria : [];
  const disqualifyingCriteria = Array.isArray(evaluation.disqualifying_criteria) ? evaluation.disqualifying_criteria : [];
  const evidenceReview = Array.isArray(evaluation.evidence_review) ? evaluation.evidence_review : [];
  const practicalTask = evaluation.practical_task?.available ? evaluation.practical_task : null;
  const practicalTaskQuestion = practicalTask ? practicalTaskPrompt(practicalTask) : '';
  const communicationScore = evaluation.communication_score || null;
  const candidateIntel = evaluation.candidate_intelligence_report || {};
  const profileEvidence = candidateIntel.profile_evidence || {};
  const candidateProfileSections = [
    ['Skills', profileEvidence.skills],
    ['Projects / domain work', profileEvidence.projects],
    ['Achievements', profileEvidence.achievements],
    ['Responsibility claims', profileEvidence.responsibility_claims],
    ['Education', profileEvidence.education],
    ['Career progression', profileEvidence.career_progression],
    ['Leadership scope', profileEvidence.leadership_scope],
  ].map(([label, items]) => ({ label, items: candidateIntelItems(items) })).filter((section) => section.items.length > 0);
  const candidateMatchedEvidence = candidateIntelItems(candidateIntel.matched_evidence, 12);
  const candidateMissingEvidence = candidateIntelItems(candidateIntel.missing_evidence, 6);
  const candidateVerificationClaims = candidateIntelItems(candidateIntel.verification_claims, 20);
  const candidateInconsistencies = candidateIntelItems(candidateIntel.inconsistencies, 4);
  const preparationReport = evaluation.preparation_report || {};
  const preparationStatus = preparationReport.preparation || evaluation.preparation_status || {};
  const objectiveCoverage = preparationReport.objective_coverage || evaluation.assessment_objective_coverage || {};
  const objectiveCoverageItems = Array.isArray(objectiveCoverage.items) ? objectiveCoverage.items : [];
  const evidenceMatrixReport = preparationReport.requirement_evidence_matrix || evaluation.requirement_evidence_matrix || {};
  const scoreLineage = Array.isArray(preparationReport.score_lineage)
    ? preparationReport.score_lineage
    : (Array.isArray(evaluation.score_lineage) ? evaluation.score_lineage : []);
  const preparationDegradedReasons = Array.isArray(preparationStatus.degraded_reasons)
    ? preparationStatus.degraded_reasons.filter(Boolean)
    : [];
  const preparationAvailable = Boolean(
    objectiveCoverageItems.length || (Array.isArray(evidenceMatrixReport.rows) && evidenceMatrixReport.rows.length)
  );
  const setupSnapshot = evaluation.setup_snapshot || {};
  const setupMode = String(setupSnapshot.mode || data.mode || metadata.mode || '').toLowerCase();
  const recruiterOnly = setupMode === 'recruiter' || setupMode === 'my_questions_only' || setupMode === 'my questions only';
  const rawCompetencyScores = Array.isArray(evaluation.competency_scores) && evaluation.competency_scores.length > 0
    ? evaluation.competency_scores
    : (Array.isArray(evaluation.criteria) ? evaluation.criteria.map((c) => ({
      competency_key: c.key,
      label: c.label,
      weight: c.weight,
      status: c.status || 'assessed',
      score_0_to_5: c.score == null ? null : (c.max === 5 ? c.score : (Number(c.score) / Math.max(1, Number(c.max || 1))) * 5),
      evidence_confidence: c.evidence_confidence,
      evidence_summary: c.reason,
      missing_evidence: c.missing_evidence || [],
      human_review_required: c.human_review_required,
    })) : []);
  const competencyScores = rawCompetencyScores.filter((c) => (
    c.priority_rank != null || String(c.label || c.competency_key || c.key || '').trim().toLowerCase() !== 'communication and relevance'
  )).sort((a, b) => (a.priority_rank ?? 10000) - (b.priority_rank ?? 10000));
  const competencyCoverage = normalizeCompetencyCoverage(rawCompetencyCoverage, competencyScores);
  const weightedCompetenciesTotal = competencyCoverage.total ?? competencyScores.length;
  const weightedCompetenciesAssessed = competencyCoverage.assessed ?? competencyScores.filter((c) => (
    String(c.status || '').toLowerCase() === 'assessed'
      || String(c.status || '').toLowerCase() === 'partially_assessed'
  )).length;
  const responseScores = Array.isArray(evaluation.response_scores) ? evaluation.response_scores : [];
  const questionStatusByQid = new Map((questionCoverage.items || []).map((item) => [String(item.qid || ''), item.status]));
  const missingEvidenceSummary = Array.isArray(evaluation.missing_evidence_summary)
    ? evaluation.missing_evidence_summary.filter((item, index, rows) => rows.findIndex((other) =>
      other.competency === item.competency && other.missing_evidence === item.missing_evidence) === index)
    : [];
  const humanReviewReasons = Array.isArray(evaluation.human_review_reasons) ? evaluation.human_review_reasons : [];
  const showJdEvidenceReview = evidenceReview.length > 0 && !(recruiterOnly && coverage.topic_basis !== 'jd_used');
  const confidenceAdjustedScoring = Number(evaluation.scoring_policy_version || 1) < 2;
  const scoreBreakdown = competencyScores.map((c) => {
    const weight = Number(c.weight || 0);
    const score5 = c.score_0_to_5 == null ? null : Number(c.score_0_to_5);
    const conf = Number(c.evidence_confidence ?? c.confidence ?? 0);
    const contribution = score5 == null ? null : Number(c.weighted_evidence_score ??
      (score5 / 5) * weight * (confidenceAdjustedScoring ? conf : 1));
    return {
      key: c.competency_key || c.key || c.label,
      label: c.label,
      weight,
      score5,
      conf,
      contribution,
      status: c.status,
      scoreStatus: c.score_status,
      evidence: c.evidence_summary || c.reason || '',
      missing: Array.isArray(c.missing_evidence) ? c.missing_evidence : (c.missing_evidence ? [c.missing_evidence] : []),
      concerns: Array.isArray(c.contradictions_or_concerns) ? c.contradictions_or_concerns : [],
      review: c.human_review_required,
    };
  });

  const Tile = ({ label, value, sub, info }) => (
    <div className="rounded-xl border border-gray-200/80 bg-gray-100/80 px-3.5 py-3 dark:border-gray-700 dark:bg-gray-800/60">
      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}{info && <span tabIndex={0} title={info} aria-label={info} className="cursor-help"><FiInfo size={12} aria-hidden="true" /></span>}</p>
      <p className="mt-1 text-xl font-bold leading-tight text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-500">{sub}</p>}
    </div>
  );

  const recIds = canShowRecording ? {
    companyId: String(recCompanyId), sessionId: String(recSessionId),
    candidateId: String(recCandidateId), interviewId: recInterviewId ? String(recInterviewId) : undefined,
  } : null;
  const SECTIONS = [
    ...(canShowRecording ? [
      { id: 'screen', label: 'Screen recording', icon: 'screen' },
      { id: 'camera', label: 'Camera recording', icon: 'camera' },
      { id: 'verification', label: 'Verification', icon: 'verification' },
      { id: 'download', label: 'Download', icon: 'download' },
    ] : []),
  ];
  const reportSections = [
    ['overview', 'Overview'], ['assessment', 'Interview assessment'],
    ['cv', 'CV & eligibility'], ['coverage', 'Coverage'],
    ['transcript', 'Transcript'], ['audit', 'Evidence detail'],
  ];
  const navigateTo = (nextSection, nextReportTab) => {
    setSection(nextSection);
    if (nextReportTab) setReportTab(nextReportTab);
    contentRef.current?.scrollTo({ top: 0 });
  };
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col lg:flex-row">
        <nav aria-label="Interview result sections" className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-gray-900 lg:w-60 lg:flex-col lg:gap-0 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:bg-gray-50/70 lg:px-3 lg:py-5 lg:dark:bg-gray-950/40">
          <p className="hidden px-3 pb-2 text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400 lg:block">Report</p>
          {reportSections.map(([id, label]) => (
            <button key={id} type="button" onClick={() => navigateTo('report', id)} aria-current={section === 'report' && reportTab === id ? 'page' : undefined}
              className={`shrink-0 whitespace-nowrap rounded-md px-3 py-2.5 text-left text-sm font-medium transition lg:w-full ${section === 'report' && reportTab === id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
              {label}
            </button>
          ))}
          {SECTIONS.length > 0 && <p className="hidden px-3 pb-2 pt-5 text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400 lg:block">Media & files</p>}
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" onClick={() => navigateTo(s.id)} aria-current={section === s.id ? 'page' : undefined}
              className={`flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2.5 text-left text-sm font-medium transition lg:w-full ${section === s.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
              <Icon name={s.icon} className="h-4 w-4 shrink-0" />
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
      <div ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50/60 px-3 py-4 dark:bg-gray-950/40 sm:px-6 sm:py-5">
        {section === 'report' && (
        <div className="space-y-5">
      <div className={reportTab === 'overview' ? 'space-y-5' : 'hidden'}>
      {(incomplete || lowCoverage) && (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 9v4m0 4h.01M10.3 3.9l-8 14A1.5 1.5 0 003.5 20h17a1.5 1.5 0 001.3-2.1l-8-14a1.5 1.5 0 00-2.6 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white">
              {endedByCandidate ? 'Candidate ended the interview early'
                : isTechnicalEnd ? 'Interview interrupted by a technical issue'
                : hasScore ? 'Scored on partial coverage — review carefully'
                : 'Not a complete assessment'}
            </p>
            <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {evaluation.coverage_warning || evaluation.error || 'The interview ended before all questions were covered.'}
              {' '}{hasScore
                ? 'The score shown is based only on this partial coverage — please read the transcript below and weigh it carefully.'
                : endedByCandidate
                  ? 'A score is not shown because too little was covered to judge fairly — please read the transcript below and decide.'
                  : 'The score and skill assessment are intentionally withheld — please read the transcript below and judge for yourself.'}
            </p>
          </div>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Verdict + at-a-glance facts */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
    <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">AI interview result</p>
              {needsReview && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Needs human review</span>
              )}
            </div>
            {onRecalculate && (
              <div className="flex flex-wrap items-center gap-2">
              <button type="button" title="Recalculate using the saved interview rubric" onClick={() => onRecalculate('saved_interview')} disabled={statusRunning || recalculating}
                className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800">
                {waiting ? 'Recalculate' : refreshRunning ? 'Recalculating…' : initialAnalysisRunning ? 'Generating…' : 'Recalculate'}
              </button>
              </div>
            )}
          </div>
      {initialAnalysisRunning && !waiting && (
            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
              Analysis is being generated for this interview. You can close this modal and reopen it; the result will appear when scoring is ready.
            </div>
          )}
          {refreshRunning && !waiting && (
            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
              Recalculating score. The current report will stay visible and update automatically.
            </div>
          )}
          {refreshFailed && (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
              {data.refresh_error || 'Recalculation failed. Please try again.'}
            </div>
          )}
          <h3 className={`mt-2 text-[1.6rem] font-bold leading-tight ${hasScore ? dt.text : 'text-gray-700 dark:text-gray-200'}`}>
            {hasScore ? (recommendationCategory || 'Evidence reviewed') : waiting ? 'Scoring pending' : initialAnalysisRunning ? 'Analysis in progress' : 'Review required'}
          </h3>
          <p className="mt-1.5 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {hasScore ? (evaluation.summary || 'AI scoring completed for this interview.') : unavailableReason}
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile label="Questions asked" value={coverageMinimumMode ? askedCount : `${askedCount}${plannedCount != null ? `/${plannedCount}` : ''}`} sub={coverageMinimumMode ? `${baselinePlannedCount} planned · ${repairAskedCount} additional` : (plannedCount != null ? 'asked / planned' : 'questions asked')} info="Scorecard and additional evidence questions asked, excluding opening, follow-ups, and the practical task." />
            <Tile label="Answers received" value={answeredCount} sub={`of ${askedCount} asked`} info="Questions with a candidate response; an answer may still lack a valid rubric evaluation." />
            <Tile label="Duration" value={durationLabel} sub={durationSubLabel} />
            <Tile label="Competencies scored" value={`${weightedCompetenciesAssessed}/${weightedCompetenciesTotal}`} sub={`${hrReport.critical_checks_unresolved || 0} critical checks unresolved`} info="A scored competency has at least one defensible rubric evaluation. This does not mean every evidence target or critical check is resolved." />
          </div>
          {weightCoverage.assessed != null && (
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
              Scored on {weightCoverage.assessed}% of {weightCoverage.configured}% configured weight.
              {Number(weightCoverage.unreached || 0) > 0 && ' Unreached weight is a coverage gap, not a zero.'}
              {hrReport.minimum_assessed_weight_percent != null && ` Completeness target: ${hrReport.minimum_assessed_weight_percent}%.`}
            </p>
          )}
          {hrReport.coverage_below_threshold && (
            <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">Below the configured coverage target; targeted reassessment is recommended.</p>
          )}
          {Number(technicalAvailability.unevaluated_answers || 0) > 0 && (
            <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
              {technicalAvailability.unevaluated_answers} answer(s) need technical scoring review; this is not a candidate-performance finding.
            </p>
          )}
        </div>

        {/* Score ring */}
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Performance score{provisionalScore ? ' · provisional' : ''}</p>
          <div className="relative mt-3" style={{ width: RING, height: RING }}>
            <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} className="-rotate-90">
              <circle cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} className="stroke-gray-100 dark:stroke-gray-800" />
              <circle cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} stroke={ringHex} strokeLinecap="round"
                strokeDasharray={CIRC} strokeDashoffset={RING_OFFSET} style={{ transition: 'stroke-dashoffset .7s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-extrabold leading-none" style={{ color: hasScore ? ringHex : '#94a3b8' }}>{hasScore ? score : '--'}</span>
              <span className="mt-0.5 text-xs text-gray-400">/ {maxScore || 100}</span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1.5">
            {[
              { label: `< ${maybeThreshold}`, on: hasScore && score < maybeThreshold, hex: '#ef4444' },
              { label: `${maybeThreshold}–${Math.max(maybeThreshold, selectThreshold - 1)}`, on: hasScore && score >= maybeThreshold && score < selectThreshold, hex: '#f59e0b' },
              { label: `${selectThreshold}+`, on: hasScore && score >= selectThreshold, hex: '#10b981' },
            ].map((b) => (
              <span key={b.label}
                className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${b.on ? '' : 'text-gray-400 dark:text-gray-500'}`}
                style={b.on ? { backgroundColor: `${b.hex}1f`, color: b.hex } : undefined}>
                {b.label}
              </span>
            ))}
          </div>
          <p className="mt-2 text-center text-[11px] leading-4 text-gray-400 dark:text-gray-500">
            Based on evaluated evidence only. Coverage gaps are shown separately.
          </p>
          {recommendationUncertainty && (
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Uncertainty: {recommendationUncertainty}
            </p>
          )}
        </div>
      </div>

      {(completionSummary.completion_reason || timeAttribution.duration_driver) && (
        <div className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 md:grid-cols-2">
          <Tile label="Completion reason" value={(completionSummary.completion_reason || '—').replaceAll('_', ' ')} sub={completionSummary.completed_at ? 'recorded by runtime' : 'runtime lifecycle'} />
          <Tile label={provisionalScore ? 'Provisional performance score' : 'Performance score'} value={hasScore ? `${score}/100` : '—'} sub="evaluated evidence only" />
          {timeAttribution.assessment_window_seconds != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 md:col-span-2">
              Assessment {Math.round(Number(timeAttribution.assessment_window_seconds) / 60)} min
              {Number(timeAttribution.unused_assessment_seconds || 0) > 0
                ? ` · ${Math.round(Number(timeAttribution.unused_assessment_seconds) / 60)} min unused`
                : ''}
              {Number(timeAttribution.closing_and_finalization_seconds || 0) > 0
                ? ` · closing/finalization ${Math.round(Number(timeAttribution.closing_and_finalization_seconds) / 60)} min`
                : ''}
            </p>
          )}
        </div>
      )}

      {/* Session signals — integrity at a glance */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Session signals</span>
          {sessionSignals.length > 0 ? sessionSignals.map((s) => (
            <span key={s.label} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>
              {s.label} <span className="tabular-nums">{s.value}</span>
            </span>
          )) : (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              No unusual signals detected
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">Integrity checks: rate-limiting · paste detection · rejoin count</p>
      </div>

      {hrReport.interview_evidence?.length > 0 && (
        <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Interview evidence at a glance</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Based on candidate answers, not CV claims.</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {hrReport.interview_evidence.slice(0, 4).map((item, index) => (
              <div key={item.qid || index} className="border-l-2 border-primary-500 pl-3 text-sm">
                <p className="font-semibold text-gray-900 dark:text-white">{item.competency || 'Interview answer'} · {item.score}/5</p>
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-600 dark:text-gray-300">“{item.candidate_passage}”</p>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => navigateTo('report', 'assessment')} className="mt-3 text-xs font-semibold text-primary-600 hover:underline dark:text-primary-300">Review all interview evidence</button>
        </section>
      )}
      </div>
      <div className={reportTab === 'assessment' ? 'space-y-5' : 'hidden'}>
      {evaluation.available && competencyScores.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Competency scorecard</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Prepared priority order · interview evidence only · unreached topics are unscored.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setScoreDetailsOpen(true)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Scoring details
              </button>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Score / 5 · Confidence · Weight</span>
            </div>
          </div>
          <div className="mt-4 grid gap-2">
            {competencyScores.map((criterion) => {
              const reportTopic = hrReport.competencies?.find((topic) => topic.key === (criterion.competency_key || criterion.key));
              const scoreValue = criterion.status === 'not_assessed' || criterion.score_0_to_5 == null
                ? null : Number(criterion.score_0_to_5);
              const ratio = scoreValue == null ? 0 : Math.min(1, Math.max(0, scoreValue / 5));
              const barHex = scoreValue == null ? '#94a3b8' : ratio >= 0.7 ? '#10b981' : ratio >= 0.5 ? '#f59e0b' : '#ef4444';
              const missing = reportTopic?.missing || (Array.isArray(criterion.missing_evidence)
                ? [...new Set(criterion.missing_evidence.filter(Boolean))] : []);
              const evidenceText = reportTopic?.evidence || criterion.evidence_summary || criterion.reason;
              const skippedQuestions = reportTopic?.skipped_questions || [];
              const skippedOnly = Boolean(reportTopic?.candidate_skipped_only);
              const status = criterion.status || 'assessed';
              const scoreStatus = criterion.score_status;
              const hasConflict = scoreStatus === 'evidence_conflict' || missing.some((reason) => /contradictory evidence/i.test(reason));
              const statusLabel = skippedOnly ? 'Candidate skipped' : scoreValue == null && status === 'partially_assessed'
                ? ['invalid_evaluation', 'evaluation_unavailable'].includes(scoreStatus)
                  ? 'Answered · scoring unavailable'
                  : hasConflict ? 'Answered · review needed' : 'Answered · insufficient evidence'
                : status.replaceAll('_', ' ');
              return (
                <div key={criterion.competency_key || criterion.key || criterion.label} className="rounded-md border border-gray-100 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-gray-800/40">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${barHex}1a`, color: barHex }}>
                        <CriterionIcon label={criterion.label} className="h-4 w-4" />
                      </span>
                      <p className="min-w-0 text-sm font-semibold text-gray-900 dark:text-white">{criterion.priority_rank ? `${criterion.priority_rank}. ` : ''}{criterion.label}{criterion.interview_mode ? <span className="ml-2 text-xs font-normal text-gray-500">{criterion.interview_mode.replaceAll('_', ' ')}</span> : null}</p>
                    </div>
                    <span className="shrink-0 text-sm font-bold tabular-nums" style={{ color: barHex }}>
                      {scoreValue == null ? '—/5' : `${scoreValue.toFixed(1)}/5`}{criterion.weight != null && <span className="ml-1.5 text-xs font-medium text-gray-400">{criterion.weight}%</span>}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-bold uppercase tracking-wide">
                    <span className={`rounded-full px-2 py-0.5 ${status === 'not_assessed' ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200' : status === 'partially_assessed' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>{statusLabel}</span>
                    {scoreValue != null && criterion.evidence_confidence != null && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-200">confidence {Math.round(Number(criterion.evidence_confidence) * 100)}%</span>}
                    {criterion.human_review_required && !skippedOnly && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">review</span>}
                    {!skippedOnly && skippedQuestions.length > 0 && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-200">{skippedQuestions.length} question{skippedQuestions.length === 1 ? '' : 's'} skipped</span>}
                  </div>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-gray-200/70 dark:bg-gray-700">
                    <div className="h-full rounded-full" style={{ width: `${ratio * 100}%`, backgroundColor: barHex }} />
                  </div>
                  {skippedOnly ? <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">The candidate chose to skip this question.</p>
                    : evidenceText && <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{evidenceText}</p>}
                  {!skippedOnly && missing.length > 0 && (
                    <p className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-300">
                      <span className="font-semibold">{scoreValue == null ? 'Why no score: ' : 'Missing evidence: '}</span>{missing.slice(0, 2).join('; ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(humanReviewReasons.length > 0 || missingEvidenceSummary.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {humanReviewReasons.length > 0 && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-5 dark:border-amber-900/50 dark:bg-amber-900/15">
              <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Uncertainty and review reasons</h3>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {humanReviewReasons.map((reason, index) => (
                  <li key={`review-${index}`} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" /><span>{reason}</span></li>
                ))}
              </ul>
            </div>
          )}
          {missingEvidenceSummary.length > 0 && !hrReport.competencies?.length && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Missing evidence</h3>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {missingEvidenceSummary.slice(0, 8).map((item, index) => (
                  <li key={`missing-${index}`} className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800/50">
                    <span className="font-semibold">{item.competency || 'Competency'}: </span>{item.missing_evidence || item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {responseScores.length > 0 && (
        <details className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-white">Question-level evidence ({responseScores.length})</summary>
          <div className="mt-4 overflow-x-auto rounded-md border border-gray-100 dark:border-gray-800">
            <div className="grid min-w-[720px] grid-cols-[minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1fr)_110px] bg-gray-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
              <span>Question asked</span>
              <span>Candidate said</span>
              <span>Evidence used</span>
              <span className="text-right">Score</span>
            </div>
            {responseScores.map((row, index) => {
              const skipped = (row.question_status || questionStatusByQid.get(String(row.planned_qid || row.question_id || ''))) === 'skipped';
              const relinked = Boolean(row.report_answer_link_changed);
              const scoreAvailable = !skipped && row.score != null && row.score_status !== 'evidence_conflict';
              const scoreValue = scoreAvailable ? Number(row.score) : null;
              const conf = scoreAvailable && row.confidence != null ? Math.round(Number(row.confidence) * 100) : null;
              const spokenPassage = candidatePassageFromRow(row);
              return (
                <details key={row.id || row.question_id || index} className="group min-w-[720px] border-t border-gray-100 text-sm dark:border-gray-800">
                  <summary className="grid cursor-pointer list-none grid-cols-[minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1fr)_110px] gap-3 px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <div className="min-w-0">
                    <p className="font-semibold leading-5 text-gray-900 dark:text-white">{row.competency_assessed || row.competency_label || 'Interview response'}</p>
                    {row.question_asked && <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{row.question_asked}</p>}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs leading-5 ${spokenPassage ? 'text-gray-700 dark:text-gray-300' : 'italic text-gray-400 dark:text-gray-500'}`}>
                      {skipped ? 'Candidate chose to skip this question.' : spokenPassage ? compactText(spokenPassage, 170) : 'Candidate passage unavailable'}
                    </p>
                  </div>
                  <div className="min-w-0">
                    {relinked ? <p className="text-xs leading-5 text-amber-700 dark:text-amber-300">Transcript answer linked for review; saved scoring was not recalculated.</p>
                      : !skipped && row.evidence_observed && <p className="line-clamp-2 text-xs leading-5 text-gray-700 dark:text-gray-300">{row.evidence_observed}</p>}
                    {!skipped && !relinked && row.missing_evidence && <p className="mt-1 line-clamp-2 text-xs leading-5 text-amber-700 dark:text-amber-300">Missing: {row.missing_evidence}</p>}
                    {Array.isArray(row.contradictions_or_concerns) && row.contradictions_or_concerns.length > 0 && (
                      <p className="mt-1 text-xs leading-5 text-rose-700 dark:text-rose-300">Concern: {row.contradictions_or_concerns[0]}</p>
                    )}
                    {row.audio_technical_quality?.status && row.audio_technical_quality.status !== 'unknown' && (
                      <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">Audio/technical: {row.audio_technical_quality.status.replaceAll('_', ' ')}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold tabular-nums text-gray-900 dark:text-white">{skipped ? 'Candidate skipped' : scoreAvailable && Number.isFinite(scoreValue) ? `${scoreValue.toFixed(1)}/5` : 'Review'}</p>
                    {conf != null && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{conf}% confidence</p>}
                    {row.human_review_required && !skipped && <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">review</span>}
                    <span className="mt-1 block text-xs text-indigo-600 group-open:hidden">View details</span>
                  </div>
                  </summary>
                  <div className="grid gap-3 border-t border-gray-100 bg-gray-50/60 px-4 py-4 text-xs leading-5 dark:border-gray-800 dark:bg-gray-800/30">
                    <p><strong>Question asked:</strong> {row.question_asked || 'Question unavailable'}</p>
                    <p className="whitespace-pre-wrap"><strong>Candidate said:</strong> {skipped ? 'Asked to skip this question.' : spokenPassage || 'Candidate passage unavailable'}</p>
                    {row.response_exchange?.length > 0 && <div className="space-y-2">
                      <h4 className="font-semibold">Complete question exchange</h4>
                      {row.response_exchange.map((turn, turnIndex) => <p key={`${turn.turn_index ?? turnIndex}-${turnIndex}`} className="whitespace-pre-wrap break-words">
                        <strong>{turn.role === 'candidate' ? 'Candidate' : turn.kind === 'followup' ? 'Interviewer follow-up' : 'Interviewer'}: </strong>{turn.text}
                      </p>)}
                    </div>}
                    {row.exchange_link_warnings?.map((warning) => <p key={warning} className="text-amber-700 dark:text-amber-300">{warning}</p>)}
                    <p><strong>Evidence used:</strong> {skipped ? 'Not scored because the candidate skipped this question.' : relinked ? 'Transcript answer linked for review; saved scoring was not recalculated.' : row.evidence_observed || 'No usable evidence recorded.'}</p>
                    {!skipped && !relinked && <p><strong>Why points were awarded:</strong> {row.points_awarded_reason || row.score_explanation?.awarded || 'Explanation unavailable.'}</p>}
                    {!skipped && !relinked && <p><strong>Why points were not awarded:</strong> {row.points_not_awarded_reason || row.score_explanation?.not_awarded || 'Explanation unavailable.'}</p>}
                    {!skipped && !relinked && <p><strong>Missing evidence:</strong> {row.missing_evidence || 'None recorded.'}</p>}
                    {row.contradictions_or_concerns?.length > 0 && <p><strong>Concerns:</strong> {row.contradictions_or_concerns.join('; ')}</p>}
                    <p><strong>Audio / technical quality:</strong> {row.audio_technical_quality?.status?.replaceAll('_', ' ') || 'Unavailable'}</p>
                  </div>
                </details>
              );
            })}
          </div>
        </details>
      )}

      </div>
      <div className={reportTab === 'cv' ? 'space-y-5' : 'hidden'}>
      {(mandatoryCriteria.length > 0 || disqualifyingCriteria.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {mandatoryCriteria.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Mandatory criteria</h3>
              <div className="mt-3 space-y-2">
                {mandatoryCriteria.map((item, index) => (
                  <div key={`mandatory-${index}`} className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold text-gray-900 dark:text-white">{item.criterion}</p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${item.status === 'met' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : item.status === 'not_met' ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>{item.status || 'unclear'}</span>
                    </div>
                    {item.evidence && <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">{item.evidence}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {disqualifyingCriteria.length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Disqualifying criteria</h3>
              <div className="mt-3 space-y-2">
                {disqualifyingCriteria.map((item, index) => (
                  <div key={`disq-${index}`} className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold text-gray-900 dark:text-white">{item.criterion}</p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${item.status === 'met' ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' : item.status === 'not_met' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>{item.status || 'unclear'}</span>
                    </div>
                    {item.evidence && <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">{item.evidence}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      </div>
      <div className={reportTab === 'assessment' ? 'space-y-5' : 'hidden'}>
      {sectionScores.length > 0 && (
        <section aria-label="Section performance and coverage" className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Section performance and coverage</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[440px] text-left text-sm">
              <thead className="border-b border-gray-200 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <tr><th className="py-2 pr-4 font-semibold">Section</th><th className="py-2 pr-4 font-semibold">Performance</th><th className="py-2 font-semibold">Weight assessed</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {sectionScores.map((item) => (
                  <tr key={item.key}>
                    <td className="py-2 pr-4 font-medium capitalize text-gray-900 dark:text-white">{String(item.key || 'Other').replaceAll('_', ' ')}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.performance_score == null ? item.score_status_label || 'Score unavailable' : `${item.performance_score}/100`}</td>
                    <td className="py-2 text-gray-700 dark:text-gray-300">{item.assessed_weight}% of {item.configured_weight}% overall</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {practicalTask && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Practical task</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {practicalTask.task?.title || 'Task response'} · {(practicalTask.status || 'submitted').replace('_', ' ')}
                {practicalTask.time_spent_seconds != null ? ` · ${Math.ceil(practicalTask.time_spent_seconds / 60)} min used` : ''}
              </p>
            </div>
            {practicalTask.evaluation?.score != null && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {practicalTask.evaluation.score}/{practicalTask.evaluation.max_score || practicalTask.evaluation.max || 10}
                {practicalTask.evaluation.confidence != null ? ` · ${Math.round(Number(practicalTask.evaluation.confidence || 0) * 100)}% confidence` : ''}
              </span>
            )}
          </div>
          <div className="mt-4 rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Task question</p>
            <p className={`mt-2 whitespace-pre-wrap leading-6 ${practicalTaskQuestion ? 'text-gray-700 dark:text-gray-200' : 'italic text-gray-400 dark:text-gray-500'}`}>
              {practicalTaskQuestion || 'Task prompt unavailable'}
            </p>
          </div>
          {practicalTask.evaluation?.summary && (
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{practicalTask.evaluation.summary}</p>
          )}
          {practicalTask.integrity_flags && Object.values(practicalTask.integrity_flags).some(Boolean) && (
            <div className="mt-3 rounded-xl bg-amber-50 p-3 text-xs leading-5 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
              <span className="font-bold">Task integrity signals: </span>
              {[
                practicalTask.integrity_flags.paste_count ? `paste events ${practicalTask.integrity_flags.paste_count}` : '',
                practicalTask.integrity_flags.pasted_characters ? `pasted characters ${practicalTask.integrity_flags.pasted_characters}` : '',
                practicalTask.integrity_flags.large_insert_count ? `large inserts ${practicalTask.integrity_flags.large_insert_count}` : '',
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {Array.isArray(practicalTask.evaluation?.rubric_results) && practicalTask.evaluation.rubric_results.length > 0 && (
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {practicalTask.evaluation.rubric_results.map((item, index) => (
                <div key={`task-rubric-${index}`} className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-gray-900 dark:text-white">{item.criterion || `Rubric ${index + 1}`}</p>
                    <span className="shrink-0 text-xs font-bold text-indigo-700 dark:text-indigo-300">
                      {item.score ?? 0}/{item.max_score ?? item.max ?? 1}
                    </span>
                  </div>
                  {item.reason && <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">{item.reason}</p>}
                </div>
              ))}
            </div>
          )}
          {Array.isArray(practicalTask.evaluation?.evidence_observed) && practicalTask.evaluation.evidence_observed.length > 0 && (
            <div className="mt-4 rounded-xl bg-emerald-50 p-3 dark:bg-emerald-950/20">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Evidence observed</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-emerald-900 dark:text-emerald-100">
                {practicalTask.evaluation.evidence_observed.map((item, index) => <li key={`task-evidence-${index}`}>{item}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(practicalTask.evaluation?.missing_evidence) && practicalTask.evaluation.missing_evidence.length > 0 && (
            <div className="mt-4 rounded-xl bg-amber-50 p-3 dark:bg-amber-950/20">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Missing evidence</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-100">
                {practicalTask.evaluation.missing_evidence.map((item, index) => <li key={`task-missing-${index}`}>{item}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(practicalTask.evaluation?.concerns) && practicalTask.evaluation.concerns.length > 0 && (
            <div className="mt-4 rounded-xl bg-rose-50 p-3 dark:bg-rose-950/20">
              <p className="text-xs font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                Concerns{practicalTask.evaluation.human_review_required ? ' · human review required' : ''}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-900 dark:text-rose-100">
                {practicalTask.evaluation.concerns.map((item, index) => <li key={`task-concern-${index}`}>{item}</li>)}
              </ul>
            </div>
          )}
          {practicalTask.answer_text && (
            <details className="mt-4 rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
              <summary className="cursor-pointer font-semibold text-gray-900 dark:text-white">View candidate task answer</summary>
              <p className="mt-3 whitespace-pre-wrap leading-6 text-gray-700 dark:text-gray-200">{practicalTask.answer_text}</p>
            </details>
          )}
        </div>
      )}

      {showJdEvidenceReview && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Evidence review</h3>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {evidenceReview.slice(0, 12).map((item, index) => (
              <div key={`evidence-${index}`} className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/50">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-gray-900 dark:text-white">{item.requirement || item.criterion}</p>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-200">{item.status || 'unclear'}</span>
                </div>
                {item.evidence && <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">{item.evidence}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(Array.isArray(evaluation.selection_reasons) || Array.isArray(evaluation.rejection_reasons)) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.isArray(evaluation.selection_reasons) && evaluation.selection_reasons.length > 0 && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5 dark:border-emerald-900/50 dark:bg-emerald-900/15">
              <h3 className="flex items-center gap-2 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 11v9M4 22h12.3a2 2 0 0 0 2-1.6l1.3-7A2 2 0 0 0 17.6 11H13l1-4.5A2.5 2.5 0 0 0 11.5 3L7 11" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span> Positive signals
              </h3>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {evaluation.selection_reasons.map((reason, index) => (
                  <li key={index} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" /><span>{reason}</span></li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(evaluation.rejection_reasons) && evaluation.rejection_reasons.length > 0 && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-5 dark:border-rose-900/50 dark:bg-rose-900/15">
              <h3 className="flex items-center gap-2 text-sm font-bold text-rose-700 dark:text-rose-300">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 9v4m0 4h.01M10.3 3.9l-8 14A1.5 1.5 0 003.5 20h17a1.5 1.5 0 001.3-2.1l-8-14a1.5 1.5 0 00-2.6 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span> Concerns to probe next round
              </h3>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {evaluation.rejection_reasons.map((reason, index) => (
                  <li key={index} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" /><span>{reason}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      </div>
      <div className={reportTab === 'cv' ? 'space-y-5' : 'hidden'}>
      <p className="text-sm text-gray-600 dark:text-gray-300">CV and profile details are verification context. They are not evidence of capability demonstrated in the interview.</p>
      {hrReport.cv_verification?.external_checks?.length > 0 && (
        <section className="border-b border-amber-200 pb-4 dark:border-amber-900/50">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Credentials pending document review</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
            {hrReport.cv_verification.external_checks.map((item, index) => (
              <li key={`${item.requirement}-${index}`}>{item.requirement}</li>
            ))}
          </ul>
        </section>
      )}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div>
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Candidate intelligence</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Structured resume/profile signals used for evidence matching and interview validation.
          </p>
        </div>
        {candidateIntel.available ? (
          <div className="mt-4 space-y-4">
            {candidateIntel.source_status !== 'validated_cv_claims' && (
              <p className="text-sm text-amber-700 dark:text-amber-300">CV interpretations require source review.</p>
            )}
            {candidateIntel.experience_verification?.status && candidateIntel.experience_verification.status !== 'unavailable' && (
              <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm leading-6 text-gray-700 dark:bg-amber-950/20 dark:text-gray-200">
                <p className="font-semibold">Experience date verification · as of {candidateIntel.experience_verification.as_of}</p>
                {(candidateIntel.experience_verification.periods || []).map((role, index) => (
                  <p key={index}>{role.company}: {role.source_dates} (about {role.months_as_of_interview} months)</p>
                ))}
                <p>{candidateIntel.experience_verification.note}</p>
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {candidateProfileSections.length > 0 ? candidateProfileSections.map((section) => (
                <div key={section.label} className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800/50">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{section.label}</p>
                  <ul className="mt-2 space-y-1.5 text-sm leading-5 text-gray-700 dark:text-gray-200">
                    {section.items.map((item, index) => (
                      <li key={`${section.label}-${index}`} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )) : (
                <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                  No structured profile evidence was captured.
                </div>
              )}
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">CV-claimed matches</p>
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                    {Array.isArray(candidateIntel.matched_evidence) ? candidateIntel.matched_evidence.length : 0}
                  </span>
                </div>
                {candidateMatchedEvidence.length > 0 ? (
                  <ul className="mt-2 space-y-1.5 text-sm leading-5 text-emerald-900 dark:text-emerald-100">
                    {candidateMatchedEvidence.map((item, index) => <li key={`matched-${index}`}>- {item}</li>)}
                  </ul>
                ) : <p className="mt-2 text-sm text-emerald-900/70 dark:text-emerald-100/70">No matched evidence rows were available.</p>}
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Missing or weak evidence</p>
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                    {Array.isArray(candidateIntel.missing_evidence) ? candidateIntel.missing_evidence.length : 0}
                  </span>
                </div>
                {candidateMissingEvidence.length > 0 ? (
                  <ul className="mt-2 space-y-1.5 text-sm leading-5 text-amber-900 dark:text-amber-100">
                    {candidateMissingEvidence.map((item, index) => <li key={`missing-intel-${index}`}>- {item}</li>)}
                  </ul>
                ) : <p className="mt-2 text-sm text-amber-900/70 dark:text-amber-100/70">No missing evidence rows were identified.</p>}
              </div>
            </div>

            {(candidateVerificationClaims.length > 0 || candidateInconsistencies.length > 0) && (
              <div className="grid gap-3 lg:grid-cols-2">
                {candidateVerificationClaims.length > 0 && (
                  <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800/50">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Claims to verify</p>
                    <ul className="mt-2 space-y-1.5 text-sm leading-5 text-gray-700 dark:text-gray-200">
                      {candidateVerificationClaims.map((item, index) => <li key={`claim-${index}`}>- {item}</li>)}
                    </ul>
                  </div>
                )}
                {candidateInconsistencies.length > 0 && (
                  <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800/50">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Inconsistencies</p>
                    <ul className="mt-2 space-y-1.5 text-sm leading-5 text-gray-700 dark:text-gray-200">
                      {candidateInconsistencies.map((item, index) => <li key={`inconsistency-${index}`}>- {item}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
            {candidateIntel.reason || 'Not available for this interview.'}
          </p>
        )}
      </div>

      </div>
      <div className={reportTab === 'audit' ? 'space-y-5' : 'hidden'}>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Assessment evidence map</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Traceable preparation objectives and evidence used to drive questions and scoring.
            </p>
          </div>
          {preparationStatus.status && (
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${String(preparationStatus.status).includes('degraded')
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
              {String(preparationStatus.status).replaceAll('_', ' ')}
            </span>
          )}
        </div>

        {preparationAvailable ? (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile label="Objectives" value={objectiveCoverage.total ?? objectiveCoverageItems.length} sub="prepared assessment targets" />
              <Tile label="Covered" value={objectiveCoverage.covered ?? 0} sub="minimum evidence met" />
              <Tile label="Partial" value={objectiveCoverage.partial ?? 0} sub="more evidence needed" />
              <Tile label="Open" value={objectiveCoverage.open ?? 0} sub={`${objectiveCoverage.not_assessable ?? 0} not assessable`} />
            </div>

            {preparationDegradedReasons.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Preparation fallbacks</p>
                <ul className="mt-2 space-y-1 text-sm text-amber-900 dark:text-amber-100">
                  {preparationDegradedReasons.map((reason, index) => <li key={`prep-reason-${index}`}>- {reason}</li>)}
                </ul>
              </div>
            )}

            {hrReport.competencies?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Evidence by scorecard topic</h4>
                {hrReport.competencies.map((topic, index) => (
                  <details key={topic.key || index} className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
                    <summary className="flex cursor-pointer items-start justify-between gap-3 text-sm font-medium text-gray-900 dark:text-white">
                      <span className="min-w-0 break-words">{topic.label}</span>
                      <span className="shrink-0">{topic.score_0_to_5 == null ? 'Not scored' : `${topic.score_0_to_5}/5`}</span>
                    </summary>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{topic.section || 'Assessment'} · {topic.status || 'unavailable'}</p>
                    {topic.evidence && <p className="mt-2 text-sm leading-5 text-gray-700 dark:text-gray-300">{topic.evidence}</p>}
                    {topic.missing?.length > 0 && <p className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-300">Still to verify: {topic.missing.join('; ')}</p>}
                  </details>
                ))}
              </div>
            )}

            {objectiveCoverageItems.length > 0 ? (
              <details className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">Technical objective ledger · {objectiveCoverageItems.length}</summary>
                <div className="mt-3 space-y-2">
                {objectiveCoverageItems.map((objective, index) => {
                  const nodes = Array.isArray(objective.evidence_nodes) ? objective.evidence_nodes : [];
                  const status = String(objective.status || 'open').replaceAll('_', ' ');
                  return (
                    <details key={objective.assessment_id || `objective-${index}`} className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-700 dark:bg-gray-800/40">
                      <summary className="cursor-pointer list-none">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{objective.requirement_text || objective.requirement || objective.competency_label || objective.competency_key || 'Assessment objective'}</p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {objective.competency_label || objective.competency_key || 'Unassigned competency'}
                              {objective.mode_labels?.length ? ` · ${objective.mode_labels.join(', ')}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {objective.priority_tier && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{objective.priority_tier}</span>}
                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600 dark:bg-gray-800 dark:text-gray-300">{status}</span>
                          </div>
                        </div>
                      </summary>
                      <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Planned questions: {(objective.planned_qids || []).join(', ') || 'None'} · Evaluated: {(objective.evaluated_qids || []).join(', ') || 'None'}
                        </p>
                        {nodes.length > 0 ? (
                          <div className="mt-3 grid gap-2 lg:grid-cols-2">
                            {nodes.map((node, nodeIndex) => (
                              <div key={node.evidence_id || `evidence-${nodeIndex}`} className="rounded-lg bg-white p-3 dark:bg-gray-900">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{node.label || node.evidence_type || 'Evidence target'}</p>
                                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-gray-800 dark:text-gray-300">{String(node.state || 'absent').replaceAll('_', ' ')}</span>
                                </div>
                                {node.observed_evidence && <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300"><span className="font-semibold">Observed:</span> {node.observed_evidence}</p>}
                                {node.missing_evidence && <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300"><span className="font-semibold">Missing:</span> {node.missing_evidence}</p>}
                                {node.confidence != null && <p className="mt-1 text-[11px] text-gray-400">Confidence {Math.round(Number(node.confidence || 0) * 100)}%</p>}
                              </div>
                            ))}
                          </div>
                        ) : <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No evidence nodes were retained for this objective.</p>}
                      </div>
                    </details>
                  );
                })}
                </div>
              </details>
            ) : (
              <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">No assessment objectives were retained in this report.</p>
            )}

            {Array.isArray(evidenceMatrixReport.rows) && evidenceMatrixReport.rows.length > 0 && (
              <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Requirement-to-evidence matrix ({evidenceMatrixReport.rows.length})
                </summary>
                <div className="mt-3 space-y-2">
                  {evidenceMatrixReport.rows.map((row, index) => {
                    const nodes = Array.isArray(row.evidence_nodes) ? row.evidence_nodes : [];
                    return (
                      <div key={`${row.requirement_id || 'requirement'}-${index}`} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                              {row.requirement_text || 'Requirement unavailable'}
                            </p>
                            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                              {row.competency_key || 'Unassigned competency'} · {row.requirement_type || 'requirement'}
                            </p>
                          </div>
                          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                            {String(row.status || 'open').replaceAll('_', ' ')}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {nodes.length > 0 ? nodes.map((node, nodeIndex) => (
                            <span key={node.evidence_id || `matrix-node-${nodeIndex}`} className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                              {node.label || node.node_type || 'Evidence'}: {String(node.state || 'absent').replaceAll('_', ' ')}
                            </span>
                          )) : <span className="text-xs text-gray-500 dark:text-gray-400">No evidence targets retained.</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}

            {scoreLineage.length > 0 && (
              <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <summary className="cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">Question-to-score lineage ({scoreLineage.length})</summary>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-xs">
                    <thead className="text-gray-500 dark:text-gray-400"><tr><th className="pb-2 pr-4">Question</th><th className="pb-2 pr-4">Assessment lineage</th><th className="pb-2 pr-4">Competency</th><th className="pb-2">Mapping</th></tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {scoreLineage.map((row, index) => (
                        <tr key={`${row.qid || 'lineage'}-${index}`}>
                          <td className="py-2 pr-4 font-mono">{row.qid || 'UNMAPPED'}</td>
                          <td className="py-2 pr-4">
                            <p className="font-mono">{row.assessment_id || 'Unavailable'}</p>
                            <p className="mt-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">Requirement: {row.requirement_id || 'Unavailable'}</p>
                            <p className="mt-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">Evidence: {(row.evidence_ids || []).join(', ') || 'None'}</p>
                          </td>
                          <td className="py-2 pr-4">{row.competency_key || 'Unavailable'}</td>
                          <td className="py-2">{row.mapping_status || 'unmapped'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        ) : (
          <p className="mt-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
            {preparationReport.message || (preparationDegradedReasons.length
              ? `Assessment evidence map unavailable: ${preparationDegradedReasons.join('; ').replaceAll('_', ' ')}.`
              : 'Assessment evidence map unavailable for this interview.')}
          </p>
        )}
      </div>

      </div>
      {/* Coverage review */}
      <div className={reportTab === 'coverage' ? 'space-y-5' : 'hidden'}>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Coverage review</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">What the AI covered during this session — straight from the transcript.</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${completedOk ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
            {completedOk && <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
            {completionLabel}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Planned questions" value={`${baselineAskedCount}/${baselinePlannedCount}`} sub={`${baselineEvaluatedCount} rubric-evaluated`} info="The baseline chosen in setup. Opening, follow-ups, and tasks are counted separately." />
          <Tile label="Additional evidence" value={repairAskedCount} sub={`${repairEvaluatedCount} rubric-evaluated`} info="Linked questions asked after the baseline for unresolved checks or evidence gaps." />
          <Tile label="Competencies scored" value={`${weightedCompetenciesAssessed}/${weightedCompetenciesTotal}`} sub={`${hrReport.critical_checks_unresolved || 0} critical checks unresolved`} info="A numeric competency score can coexist with optional missing evidence. Critical checks are separate from scoring." />
          {practicalTasksPlanned > 0
            ? <Tile label="Practical task" value={`${practicalTasksSubmitted}/${practicalTasksPlanned}`} sub={`${practicalTasksEvaluated} evaluated`} info="Submitted and evaluated practical tasks; not included in normal question counts." />
            : <Tile label="Answers received" value={answeredCount} sub="candidate replies" info="A received answer is not necessarily rubric-evaluated." />}
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Assessment questions: {askedCount} asked · {answeredCount} answered · {evaluatedQuestionCount} rubric-evaluated. Opening: {Number(sourceCoverage.opening?.asked || 0)} separate turns.
        </p>
        {(skippedQuestionCount > 0 || notReachedQuestionCount > 0) && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Candidate skipped: {skippedQuestionCount} · Not reached: {notReachedQuestionCount}. These are coverage states, not candidate failures.
          </p>
        )}
        {followupOrReaskCount > 0 && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Follow-ups, re-asks, or connection checks recorded separately: {followupOrReaskCount}.
          </p>
        )}
        {(repairAskedCount > 0 || repairPlannedCount > 0) && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Additional evidence questions after the baseline: {repairAskedCount}{repairPlannedCount ? `/${repairPlannedCount}` : ''}.
          </p>
        )}
        {sourceCoverageRows.length > 0 && (
          <details className="mt-4">
            <summary className="mb-2 cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Question count breakdown</summary>
            <div className="grid gap-2 sm:grid-cols-3">
              {sourceCoverageRows.map((row) => (
                <div key={row.key} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{row.label}</p>
                  <p className="mt-1 text-sm font-bold text-gray-900 dark:text-white">
                    {Number(row.data?.[row.metric] || 0)}/{Number(row.data?.planned || 0)}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {Number(row.data?.evaluated || 0)} evaluated
                  </p>
                </div>
              ))}
            </div>
            {Number(hrQuestions.followups ?? sourceCoverage.followups?.asked ?? 0) > 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Follow-ups asked: {Number(hrQuestions.followups ?? sourceCoverage.followups?.asked ?? 0)}. Follow-ups are excluded from planned-question totals.
              </p>
            )}
          </details>
        )}
        {progressionIssue && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <span className="font-semibold">Progression diagnostic: </span>
            {progressionIssue === 'TASK_FOLLOWUP_LOOP'
              ? 'Repeated follow-ups after the practical task prevented the remaining planned questions from being reached.'
              : String(progressionIssue).replaceAll('_', ' ').toLowerCase()}
          </div>
        )}
        {coverageCause && (topicsAssessedCount < topicsRequiredCount || Number(hrReport.critical_checks_unresolved || 0) > 0) && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <span className="font-semibold">Checks and gaps: </span>{coverageCause}
          </p>
        )}
        {topicsStatus.length > 0 && !(Array.isArray(competencyCoverage.items) && competencyCoverage.items.length > 0) ? (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {recruiterOnly ? 'Planned recruiter coverage' : 'Scorecard topics'}
            </p>
            <ul className="space-y-1.5">
              {topicsStatus.map((t) => {
                const meta = t.status === 'assessed'
                  ? { icon: 'check', color: 'text-emerald-600 dark:text-emerald-400', note: 'rubric score available' }
                  : t.status === 'asked'
                    ? { icon: 'warning', color: 'text-amber-600 dark:text-amber-400', note: 'asked, but answer too thin to assess' }
                    : { icon: 'close', color: 'text-rose-500 dark:text-rose-400', note: 'never asked' };
                return (
                  <li key={`ts-${t.skill}`} className="flex items-center gap-2 text-sm">
                    <Icon name={meta.icon} className={`h-4 w-4 shrink-0 ${meta.color}`} />
                    <span className="font-medium text-gray-800 dark:text-gray-200">{t.skill}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">— {meta.note}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : coveredSkills.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Covered</p>
            <div className="flex flex-wrap gap-2">
              {coveredSkills.map((s) => (
                <span key={`cov-${s}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">{s}</span>
              ))}
            </div>
          </div>
        )}
        {Array.isArray(competencyCoverage.items) && competencyCoverage.items.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Weighted competency coverage</p>
            <div className="grid gap-2 md:grid-cols-2">
              {competencyCoverage.items.map((item) => (
                <div key={item.key || item.label} className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-800/50">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{item.label || 'Competency'}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      item.status === 'assessed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : item.status === 'partially_assessed' || item.status === 'insufficient_evidence' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200'
                    }`}>{String(item.status || 'not_assessed').replaceAll('_', ' ')}</span>
                  </div>
                  <p className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    Weight {Number(item.weight || 0)}%
                    {` · ${item.planned_qids?.length || 0} planned · ${item.evaluated_qids?.length || 0} rubric-evaluated`}
                  </p>
                  {item.reason && <p className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">{item.reason}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
        {Array.isArray(coverage.unasked_categories) && coverage.unasked_categories.length > 0 && !coverage.unrestricted && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Not reached</p>
            <div className="flex flex-wrap gap-2">
              {coverage.unasked_categories.map((item) => (
                <span key={`nr-${item.label}`} className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {item.label}{item.count > 1 ? ` ×${item.count}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {communicationScore && (
        <div className="rounded-2xl border border-sky-100 bg-sky-50/60 p-5 shadow-sm dark:border-sky-900/50 dark:bg-sky-900/15">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-sky-900 dark:text-sky-100">Communication score</h3>
              <p className="mt-1 text-xs leading-5 text-sky-700 dark:text-sky-300">
                {communicationScore.basis || "Generated from the candidate's spoken/text transcript across the interview."}
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-700 shadow-sm dark:bg-sky-950/40 dark:text-sky-200">
              {communicationScore.score != null ? `${communicationScore.score}/${communicationScore.max_score || 5}` : 'Not assessed'}
              {communicationScore.confidence != null ? ` · ${Math.round(Number(communicationScore.confidence || 0) * 100)}% confidence` : ''}
            </span>
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl bg-white/70 p-3 dark:bg-sky-950/20">
              <p className="text-xs font-bold uppercase tracking-wide text-sky-700 dark:text-sky-300">Evidence used</p>
              <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {communicationScore.evidence_observed || 'No usable communication evidence was captured.'}
              </p>
            </div>
            <div className="rounded-xl bg-white/70 p-3 dark:bg-sky-950/20">
              <p className="text-xs font-bold uppercase tracking-wide text-sky-700 dark:text-sky-300">Missing evidence</p>
              <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-200">
                {communicationScore.missing_evidence || 'No major communication evidence gaps were identified.'}
              </p>
            </div>
          </div>
        </div>
      )}

      </div>
      {/* Conversational transcript */}
      <div className={reportTab === 'transcript' ? 'space-y-5' : 'hidden'}>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Conversational transcript</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Formatted from the AI interview attempt so the exchange is easy to review.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{transcriptEntries.length} messages</span>
            {transcriptEntries.length > 6 && (
              <button type="button" onClick={() => setExpanded((p) => !p)}
                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                {expanded ? 'Collapse' : 'Expand all'}
              </button>
            )}
            <button type="button"
              onClick={() => { try { navigator.clipboard?.writeText(transcriptEntries.map((e) => `${isCandidateEntry(e) ? (e.speaker && e.speaker !== 'Candidate' ? e.speaker : 'Candidate') : 'Wiza'}: ${e.text}`).join('\n\n')); } catch { /* ignore */ } }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" strokeLinecap="round" /></svg>
              Copy
            </button>
          </div>
        </div>
        {transcriptEntries.length === 0 ? (
          <p className="rounded-xl bg-gray-50 p-5 text-sm text-gray-500 dark:bg-gray-800/50">No transcript is available yet.</p>
        ) : (
          <div className={`${expanded ? 'max-h-[64vh]' : 'max-h-[30rem]'} space-y-5 overflow-y-auto px-1`}>
            {visible.map((entry, index) => {
              const cand = isCandidateEntry(entry);
              const who = cand ? (entry.speaker && entry.speaker !== 'Candidate' ? entry.speaker : 'Candidate') : 'Wiza';
              const initial = (who || '?').trim().charAt(0).toUpperCase();
              return (
                <div key={`${entry.timeStr}-${index}`} className={`flex items-start gap-2.5 ${cand ? 'flex-row-reverse' : ''}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm"
                    style={{ backgroundColor: cand ? primary : '#94a3b8' }}>{cand ? initial : 'W'}</span>
                  <div className="max-w-[80%]">
                    <div className={`mb-1 flex items-center gap-2 ${cand ? 'flex-row-reverse' : ''}`}>
                      <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">{who}</span>
                      {entry.pasted && cand && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Pasted</span>
                      )}
                      {entry.timeStr && <span className="text-[10px] text-gray-300 dark:text-gray-600">{entry.timeStr}</span>}
                    </div>
                    <div className={`rounded-2xl px-4 py-2.5 text-sm leading-6 ${cand ? 'rounded-tr-md text-white shadow-sm' : 'rounded-tl-md border border-gray-100 text-gray-700 dark:border-gray-700/60 dark:text-gray-200'}`}
                      style={cand ? { backgroundColor: primary } : { backgroundColor: `${primary}0d` }}>
                      <p className="whitespace-pre-wrap">{entry.text}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            {!expanded && transcriptEntries.length > visible.length && (
              <div className="pt-1 text-center">
                <button type="button" onClick={() => setExpanded(true)}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800">
                  Show {transcriptEntries.length - visible.length} more messages
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
        )}
        {section === 'screen' && recIds && (
          <RecordingPlayer kind="screen" companyId={recIds.companyId} sessionId={recIds.sessionId} candidateId={recIds.candidateId} interviewId={recIds.interviewId} />
        )}
        {section === 'camera' && recIds && (
          <RecordingPlayer kind="camera" companyId={recIds.companyId} sessionId={recIds.sessionId} candidateId={recIds.candidateId} interviewId={recIds.interviewId} />
        )}
        {section === 'verification' && recIds && (
          <VerificationSection companyId={recIds.companyId} sessionId={recIds.sessionId} candidateId={recIds.candidateId} />
        )}
        {section === 'download' && recIds && (
          <DownloadSection companyId={recIds.companyId} sessionId={recIds.sessionId} candidateId={recIds.candidateId} interviewId={recIds.interviewId} candidateName={metadata.candidate_name} />
        )}
        {scoreDetailsOpen && (
          <div className="fixed inset-0 z-[120] flex justify-end bg-slate-950/45 backdrop-blur-sm" onClick={() => setScoreDetailsOpen(false)}>
            <aside
              className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-2xl dark:bg-gray-950"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Scoring explanation</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                    {confidenceAdjustedScoring
                      ? 'This saved result uses the earlier confidence-adjusted scoring policy.'
                      : 'Performance uses rubric scores and configured weights. Confidence is shown separately.'}
                  </p>
                </div>
                <button type="button" onClick={() => setScoreDetailsOpen(false)}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                  Close
                </button>
              </div>
              <div className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-900">
                <p className="font-semibold text-gray-900 dark:text-white">Overall math</p>
                <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
                  Performance score uses evaluated evidence only: {hasScore ? `${score}/100` : 'Unavailable'}.
                  {weightCoverage.assessed != null && ` Weighted points are divided by ${weightCoverage.assessed}% assessed weight.`}
                </p>
              </div>
              <div className="mt-4 space-y-3">
                {scoreBreakdown.map((row) => (
                  <div key={row.key} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{row.label}</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {row.status?.replaceAll('_', ' ') || 'assessed'} · confidence {Math.round((row.conf || 0) * 100)}%
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        {row.score5 == null ? 'NA' : `${row.score5.toFixed(1)}/5`} · {row.weight}%
                      </span>
                    </div>
                    <p className="mt-3 text-xs leading-5 text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">Contribution: </span>
                      {row.score5 == null
                        ? 'Not assessed; excluded from the performance score.'
                        : confidenceAdjustedScoring
                          ? `${row.score5.toFixed(1)}/5 × ${row.weight}% × ${Math.round((row.conf || 0) * 100)}% confidence = ${row.contribution.toFixed(2)} weighted points`
                          : `${row.score5.toFixed(1)}/5 × ${row.weight}% = ${row.contribution.toFixed(2)} weighted points`}
                    </p>
                    {row.evidence && (
                      <p className="mt-2 text-xs leading-5 text-emerald-700 dark:text-emerald-300">
                        <span className="font-semibold">Points awarded for: </span>{row.evidence}
                      </p>
                    )}
                    {row.missing.length > 0 && (
                      <p className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-300">
                        <span className="font-semibold">Points reduced for: </span>{row.missing.slice(0, 3).join('; ')}
                      </p>
                    )}
                    {row.concerns.length > 0 && (
                      <p className="mt-2 text-xs leading-5 text-rose-700 dark:text-rose-300">
                        <span className="font-semibold">Concerns: </span>{row.concerns.slice(0, 3).join('; ')}
                      </p>
                    )}
                    {row.review && <p className="mt-2 text-xs font-semibold text-amber-700 dark:text-amber-300">Human review recommended for this row.</p>}
                  </div>
                ))}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Manual meeting-analysis body ────────────────────────────────────────────────────────
function ManualRecordingPlayer({ interviewId }) {
  const [failed, setFailed] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setFailed(false);
    setRecordingUrl('');
    // Native video requests cannot send the app's authentication headers.
    aiAutomationApi.downloadBlob(`/interviews/${encodeURIComponent(String(interviewId))}/video-stream`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setRecordingUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [interviewId]);

  return (
    <section className="border-b border-gray-200 pb-4 dark:border-gray-700">
      <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Interview recording</h4>
      {failed ? (
        <p role="status" className="text-sm text-gray-500 dark:text-gray-400">Recording unavailable.</p>
      ) : !recordingUrl ? (
        <p role="status" className="text-sm text-gray-500 dark:text-gray-400">Loading recording...</p>
      ) : (
        <video
          controls
          playsInline
          preload="metadata"
          aria-label="Manual interview recording"
          src={recordingUrl}
          onError={() => setFailed(true)}
          className="aspect-video w-full max-h-[480px] rounded-lg bg-black object-contain"
        >
          Your browser does not support video playback.
        </video>
      )}
    </section>
  );
}

function ManualAnalysisView({ analysis, transcript = '', interviewId = null }) {
  const a = analysis || {};
  const summary = a.summary || a.analysis_summary || a.overall_summary || '';
  const recommendation = a.recommendation || a.decision || '';
  const strengths = Array.isArray(a.strengths) ? a.strengths : (Array.isArray(a.pros) ? a.pros : []);
  const concerns = Array.isArray(a.concerns) ? a.concerns : (Array.isArray(a.risks) ? a.risks : []);
  const keyPoints = Array.isArray(a.key_points) ? a.key_points : (Array.isArray(a.highlights) ? a.highlights : []);
  const rawScore = a.skills_matching_score ?? a.match_score ?? a.matching_score;
  const score = Number.isFinite(Number(rawScore)) ? Number(rawScore) : null;
  const matchLabel = score == null ? '' : (score >= 8 ? 'Excellent Match' : score >= 6 ? 'Good Match' : 'Poor Match');
  const matchBadge = score == null ? '' : (score >= 8
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
    : score >= 6
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300');
  const matchBorder = score == null ? '' : (score >= 8
    ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300'
    : score >= 6
      ? 'border-amber-300 text-amber-700 dark:text-amber-300'
      : 'border-rose-300 text-rose-700 dark:text-rose-300');
  const hasStructured = score != null || summary || recommendation || strengths.length || concerns.length || keyPoints.length;
  const transcriptSection = typeof transcript === 'string' && transcript.trim() ? (
    <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
      <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Transcript</h4>
      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-700 dark:text-gray-300">{transcript}</p>
    </section>
  ) : null;

  const List = ({ items, dot }) => (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
          <span className={`mt-0.5 ${dot}`}>•</span>
          <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
        </li>
      ))}
    </ul>
  );

  if (!hasStructured) {
    return (
      <div className="space-y-4">
        {interviewId && <ManualRecordingPlayer key={interviewId} interviewId={interviewId} />}
        {Object.keys(a).length > 0 && (
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
            {JSON.stringify(a, null, 2)}
          </pre>
        )}
        {transcriptSection}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {interviewId && <ManualRecordingPlayer key={interviewId} interviewId={interviewId} />}
      {score != null ? (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Skills matching score</p>
          <div className="flex items-start gap-4">
            <div className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl border-2 ${matchBorder}`}>
              <span className="text-xl font-bold leading-none">{score.toFixed(1)}</span>
              <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">/ 10</span>
            </div>
            <div className="min-w-0">
              {matchLabel && <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${matchBadge}`}>{matchLabel}</span>}
              {summary && <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{summary}</p>}
            </div>
          </div>
        </div>
      ) : summary ? (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Summary</h4>
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">{summary}</p>
        </div>
      ) : null}
      {recommendation && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Recommendation</h4>
          <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold capitalize text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">{recommendation}</span>
        </div>
      )}
      {(strengths.length > 0 || concerns.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {strengths.length > 0 && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-900/20">
              <h4 className="mb-3 text-sm font-semibold text-emerald-900 dark:text-emerald-200">Strengths</h4>
              <List items={strengths} dot="text-emerald-500" />
            </div>
          )}
          {concerns.length > 0 && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-5 dark:border-red-900 dark:bg-red-900/20">
              <h4 className="mb-3 text-sm font-semibold text-red-900 dark:text-red-200">Concerns</h4>
              <List items={concerns} dot="text-red-500" />
            </div>
          )}
        </div>
      )}
      {keyPoints.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Key points</h4>
          <List items={keyPoints} dot="text-gray-400" />
        </div>
      )}
      {transcriptSection}
    </div>
  );
}

// ── Modal shell ─────────────────────────────────────────────────────────────────────────
export default function MeetingAnalysisModal({
  open,
  onClose,
  candidate = null,
  title = 'Interview Report',
  aiResult = null,
  analysis = null,
  transcript = '',
  manualInterviewId = null,
  loading = false,
  error = null,
  onRecalculate = null,
  recalculating = false,
  onReject = null,
  onShortlist = null,
  onAdvance = null,
  scheduledInterviewId = null,
}) {
  const liveResult = useInterviewResult(open, scheduledInterviewId);
  if (scheduledInterviewId) {
    aiResult = liveResult.result;
    loading = liveResult.loading;
    error = liveResult.error;
    recalculating = liveResult.recalculating;
    onRecalculate = liveResult.recalculate;
    analysis = null;
  }
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { ianaTimezone } = useTimezone();
  const primaryColor = settings?.theme?.colors?.primary || '#4f46e5';
  if (!open) return null;

  const candidateName = candidate?.name || candidate?.candidate_name || candidate?.email || 'Candidate';
  const candidateEmail = candidate?.email || candidate?.candidate_email || '';
  const headerInitials = String(candidateName || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  const _role = candidate?.role || candidate?.job_title || candidate?.designation || candidate?.position || (title && title !== 'Interview Report' ? title : '');
  const _scheduledDate = candidate?.scheduled_date || candidate?.calendar?.scheduled_date || aiResult?.scheduled_date || '';
  const _scheduledTime = candidate?.scheduled_time || candidate?.calendar?.scheduled_time || aiResult?.scheduled_time || '';
  const _startedAt = aiResult?.metadata?.started_at || aiResult?.metadata?.ended_at || candidate?.scheduled_at || '';
  let _dateStr = '';
  if (_scheduledDate) {
    const d = new Date(`${_scheduledDate}T00:00:00`);
    const datePart = Number.isNaN(d.getTime())
      ? String(_scheduledDate)
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    _dateStr = [datePart, fmtTime(_scheduledTime)].filter(Boolean).join(' · ');
  } else if (_startedAt) {
    const d = new Date(_startedAt);
    if (!Number.isNaN(d.getTime())) {
      const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
      if (ianaTimezone) {
        dateOptions.timeZone = ianaTimezone;
        timeOptions.timeZone = ianaTimezone;
      }
      _dateStr = `${d.toLocaleDateString(undefined, dateOptions)} · ${d.toLocaleTimeString(undefined, timeOptions)}`;
    }
  }
  const headerMeta = [candidateEmail, _role, _dateStr, aiResult ? 'AI interview' : ''].filter(Boolean).join(' · ');

  const body = loading ? (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500 dark:text-gray-400">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-transparent" style={{ borderTopColor: primaryColor }} />
      Loading interview analysis…
    </div>
  ) : error && !aiResult ? (
    <div className="py-12 text-center">
      <div className="mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
        {error}
        {scheduledInterviewId && <button type="button" onClick={liveResult.checkStatus} className="ml-3 underline">Check status</button>}
      </div>
    </div>
  ) : aiResult ? (
    <>
      {error && String(aiResult.evaluation_status || aiResult.refresh_status) !== 'failed' && (
        <div role="alert" className="px-6 py-3 text-sm text-red-700">{error}
          {scheduledInterviewId && <button type="button" onClick={liveResult.checkStatus} className="ml-3 underline">Check status</button>}
        </div>
      )}
      {liveResult.waiting && <div role="status" className="flex items-center gap-3 px-6 py-3 text-sm">
        Scoring is still in progress
        <button type="button" onClick={liveResult.checkStatus} disabled={liveResult.checking} className="rounded border px-3 py-1">Check status</button>
      </div>}
      <AIInterviewResultView result={aiResult} onRecalculate={onRecalculate} recalculating={recalculating} waiting={liveResult.waiting} />
    </>
  ) : scheduledInterviewId && liveResult.waiting ? (
    <div role="status" className="p-6 text-sm">Scoring is still in progress <button type="button" onClick={liveResult.checkStatus} className="ml-3 underline">Check status</button></div>
  ) : analysis || transcript || manualInterviewId ? (
    <ManualAnalysisView analysis={analysis} transcript={transcript} interviewId={manualInterviewId} />
  ) : (
    <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">No analysis is available for this candidate yet.</div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-start justify-center overflow-y-auto bg-gray-950/60 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="my-auto flex h-[90vh] w-[96vw] max-w-[1600px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header — native-theme accent */}
        <div
          className="relative flex shrink-0 flex-col gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:px-6 sm:py-4"
          style={{ background: isDark
            ? `linear-gradient(90deg, ${primaryColor}22 0%, transparent 60%)`
            : `linear-gradient(90deg, ${primaryColor}14 0%, transparent 60%)` }}
        >
          <div className="flex min-w-0 items-center gap-3 pr-9 sm:pr-0">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm" style={{ background: primaryColor }}>
              {headerInitials}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-gray-900 dark:text-white">{aiResult ? candidateName : title}</h2>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{aiResult ? headerMeta : (candidateName + (candidateEmail ? ` · ${candidateEmail}` : ''))}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-start gap-2 sm:justify-end">
            {aiResult && (
              <>
                <button type="button" onClick={onReject || undefined} title={onReject ? 'Reject' : 'Coming soon'}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-rose-900/60 dark:bg-gray-900 dark:text-rose-300 dark:hover:bg-rose-900/20">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" /></svg>
                  Reject
                </button>
                {/* Shortlist / Advance are not wired yet — disabled for now with a "Coming soon" tooltip.
                    They auto-enable if a caller ever passes onShortlist / onAdvance. */}
                <button type="button" onClick={onShortlist || undefined} disabled={!onShortlist}
                  title={onShortlist ? 'Shortlist' : 'Coming soon'}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:hover:bg-gray-900">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M6 3h12a1 1 0 0 1 1 1v16l-7-4-7 4V4a1 1 0 0 1 1-1z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Shortlist
                </button>
                <button type="button" onClick={onAdvance || undefined} disabled={!onAdvance}
                  title={onAdvance ? 'Advance to next round' : 'Coming soon'}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50"
                  style={{ backgroundColor: primaryColor }}>
                  <span className="hidden sm:inline">Advance to next round</span><span className="sm:hidden">Advance</span>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              </>
            )}
            <button type="button" onClick={onClose} aria-label="Close"
              className="absolute right-3 top-3 rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 sm:static">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={aiResult && !loading && !error
          ? 'min-h-0 flex-1 overflow-hidden'
          : 'min-h-0 flex-1 overflow-y-auto bg-gray-50/60 px-3 py-4 dark:bg-gray-950/40 sm:px-6 sm:py-5'}
          style={{ backgroundColor: isDark ? `${primaryColor}12` : `${primaryColor}0a` }}>
          {body}
        </div>
      </div>
    </div>,
    document.body,
  );
}
